import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft, CircleCheck, FileText, Globe } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  {
    key: "webpdf",
    testid: "deck-dl-webpdf",
    icon: FileText,
    title: "Webpage as PDF",
    sub: "Fully rendered — one slide per page",
  },
  {
    key: "html",
    testid: "deck-dl-html",
    icon: Globe,
    title: "Webpage as HTML",
    sub: "Standalone file — opens in any browser",
  },
];

export default function DeckAccess() {
  const [code, setCode] = useState("");
  const [granted, setGranted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const dl = (key) =>
    `${API}/deck/download?file_type=${key}&code=${encodeURIComponent(code.trim())}`;

  const unlock = async (e) => {
    e.preventDefault();
    if (busy || !code.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/deck/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) throw new Error("bad code");
      setGranted(true);
      toast.success("Access granted — choose a format below.");
    } catch {
      setError("That code isn't right. Please check it and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-bone font-sans text-inktext">
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-mint"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-3xl flex-col px-6">
        <header className="flex h-20 items-center justify-between">
          <Link to="/" data-testid="deck-page-logo" aria-label="PranIQ — back to site">
            <Logo />
          </Link>
          <Link
            to="/"
            data-testid="deck-page-back"
            className="flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-sm font-medium text-ink transition-colors hover:border-ink/50"
          >
            <ArrowLeft size={15} />
            Back to site
          </Link>
        </header>

        <main className="flex flex-1 items-center justify-center py-10">
          <div className="w-full rounded-3xl border border-line bg-white p-8 shadow-[0_48px_90px_-60px_rgba(14,36,64,0.4)] md:p-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint text-navy">
              {granted ? <CircleCheck size={24} /> : <Lock size={22} />}
            </div>
            <h1 className="mt-6 font-display text-3xl font-medium tracking-tight md:text-4xl">
              Share this webpage
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-mut md:text-base">
              This page is private. Enter the access code you were given to
              download the full presentation as a PDF or HTML file.
            </p>

            <form
              onSubmit={unlock}
              className="mt-8"
              data-testid="deck-access-form"
            >
              <label
                htmlFor="deck-code"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-navy"
              >
                Access code
              </label>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  id="deck-code"
                  data-testid="deck-code-input"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError("");
                  }}
                  placeholder="e.g. XXXX-XXXX-XXXX"
                  className="w-full rounded-xl border border-line bg-bone px-4 py-3 text-sm text-inktext placeholder:text-ink/35 focus:border-navy focus:ring-2 focus:ring-navy/20 sm:max-w-xs"
                  autoComplete="off"
                />
                <button
                  data-testid="deck-unlock-btn"
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-medium text-bone transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                >
                  <Lock size={15} />
                  {busy ? "Checking…" : "Unlock"}
                </button>
              </div>
              {error && (
                <p
                  data-testid="deck-error-msg"
                  className="mt-3 text-sm font-medium text-red-600"
                >
                  {error}
                </p>
              )}
            </form>

            {granted && (
              <div
                data-testid="deck-granted-msg"
                className="mt-8 space-y-3 rounded-2xl border border-line bg-bone2 p-5"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-inktext">
                  <CircleCheck size={17} className="text-gold" />
                  Access granted — download the webpage in your format:
                </div>
                {FORMATS.map((f) => (
                  <a
                    key={f.key}
                    data-testid={f.testid}
                    href={dl(f.key)}
                    className="flex items-center gap-4 rounded-xl border border-line bg-white px-5 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-30px_rgba(14,36,64,0.5)]"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mint text-navy">
                      <f.icon size={17} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-inktext">
                        {f.title}
                      </span>
                      <span className="block text-xs text-mut">{f.sub}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </main>

        <footer className="flex items-center justify-between py-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
            Praniq Healthtech Private Limited
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">
            Private document
          </span>
        </footer>
      </div>
    </div>
  );
}
